import React, { useState } from 'react';
import { MapPin, Phone, Mail, MessageCircle, Send, ArrowRight, Bus } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { ScrollReveal } from '../components/ScrollReveal';
import './ContactsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [formStatus, setFormStatus] = useState({ loading: false, success: false, error: null });

  // Newsletter state
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState({ loading: false, success: false });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) {
      setFormStatus({ loading: false, success: false, error: 'Por favor, aceite a Política de Privacidade.' });
      return;
    }
    setFormStatus({ loading: true, success: false, error: null });

    try {
      await axios.post(`${API}/submissions/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message
      });
      
      setFormStatus({ loading: false, success: true, error: null });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setPrivacyAccepted(false);
      
      setTimeout(() => {
        setFormStatus(prev => ({ ...prev, success: false }));
      }, 5000);
    } catch (error) {
      setFormStatus({ loading: false, success: false, error: 'Ocorreu um erro ao enviar a mensagem. Por favor, tente novamente.' });
    }
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    setNewsletterStatus({ loading: true, success: false });
    // Simulate newsletter subscription
    setTimeout(() => {
      setNewsletterStatus({ loading: false, success: true });
      setNewsletterEmail('');
      setTimeout(() => {
        setNewsletterStatus({ loading: false, success: false });
      }, 3000);
    }, 800);
  };

  return (
    <div className="contacts-page-new" data-testid="contacts-page">
      <SEO 
        title="Contactos"
        description="Entre em contacto com a AASPS. Estamos disponíveis para ajudar. Encontre a nossa localização, telefone e formulário de contacto."
        keywords="contactos, AASPS, Barcelos, telefone, email, localização, formulário"
        url="/contactos"
      />
      <Header />
      <Breadcrumbs />
      
      <main className="contacts-main">
        <div className="contacts-container-new">
          {/* Left Column - Contact Info */}
          <div className="contacts-info-column">
            <h1 className="contacts-title">Fale Connosco</h1>
            <p className="contacts-intro">
              Estamos disponíveis para esclarecer qualquer dúvida. Entre em contacto connosco através dos canais abaixo ou preencha o formulário.
            </p>
            
            <div className="contact-items">
              <div className="contact-item" data-testid="contact-location">
                <div className="contact-item-icon">
                  <MapPin size={20} />
                </div>
                <div className="contact-item-content">
                  <span className="contact-item-label">Localização</span>
                  <span className="contact-item-value">Rua do Cruzeiro, 194 B<br />1300-172 Lisboa</span>
                </div>
              </div>
              
              <div className="contact-item" data-testid="contact-phone">
                <div className="contact-item-icon">
                  <Phone size={20} />
                </div>
                <div className="contact-item-content">
                  <span className="contact-item-label">Telefone</span>
                  <a href="tel:+351213630884" className="contact-item-value">+351 213 630 884</a>
                </div>
              </div>
              
              <div className="contact-item" data-testid="contact-email">
                <div className="contact-item-icon">
                  <Mail size={20} />
                </div>
                <div className="contact-item-content">
                  <span className="contact-item-label">E-mail</span>
                  <a href="mailto:geral@aasps.pt" className="contact-item-value">geral@aasps.pt</a>
                </div>
              </div>
              
              <div className="contact-item" data-testid="contact-whatsapp">
                <div className="contact-item-icon">
                  <MessageCircle size={20} />
                </div>
                <div className="contact-item-content">
                  <span className="contact-item-label">Horário</span>
                  <span className="contact-item-value">Segunda a Sexta<br />09h00 - 17h00</span>
                </div>
              </div>
              
              <div className="contact-item contact-item-transport" data-testid="contact-transport">
                <div className="contact-item-icon">
                  <Bus size={20} />
                </div>
                <div className="contact-item-content">
                  <span className="contact-item-label">Transportes (Carris)</span>
                  <span className="contact-item-value transport-inline">760 · 742 · 711 · 18E · 729 · 732</span>
                </div>
              </div>
            </div>
            
            {/* Newsletter Box */}
            <div className="newsletter-box" data-testid="newsletter-box">
              <div className="newsletter-icon">
                <Mail size={22} />
              </div>
              <div className="newsletter-content">
                <h3>Newsletter</h3>
                <p>Subscreva e receba novidades exclusivas</p>
                {newsletterStatus.success ? (
                  <div className="newsletter-success">Subscrito com sucesso!</div>
                ) : (
                  <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
                    <input
                      type="email"
                      placeholder="O seu e-mail"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      required
                      data-testid="newsletter-email"
                    />
                    <button type="submit" disabled={newsletterStatus.loading} data-testid="newsletter-submit">
                      {newsletterStatus.loading ? '...' : 'Subscrever'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
          
          {/* Right Column - Contact Form */}
          <div className="contacts-form-column">
            {formStatus.success ? (
              <div className="form-success-new" data-testid="form-success">
                <div className="success-icon">
                  <Send size={32} />
                </div>
                <h3>Mensagem Enviada!</h3>
                <p>Obrigado pelo seu contacto. Responderemos brevemente.</p>
              </div>
            ) : (
              <form className="contact-form-new" onSubmit={handleSubmit} data-testid="contact-form">
                <div className="form-row-new">
                  <div className="form-field-new">
                    <label>NOME *</label>
                    <input
                      type="text"
                      name="name"
                      placeholder="Nome completo"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="input-name"
                    />
                  </div>
                  <div className="form-field-new">
                    <label>E-MAIL *</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="exemplo@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="input-email"
                    />
                  </div>
                </div>
                
                <div className="form-row-new">
                  <div className="form-field-new">
                    <label>TELEFONE *</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+351"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="input-phone"
                    />
                  </div>
                  <div className="form-field-new">
                    <label>ASSUNTO *</label>
                    <input
                      type="text"
                      name="subject"
                      placeholder="Assunto da mensagem"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      data-testid="input-subject"
                    />
                  </div>
                </div>
                
                <div className="form-field-new full-width">
                  <label>MENSAGEM</label>
                  <textarea
                    name="message"
                    placeholder="Descreva o serviço que necessita..."
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    data-testid="input-message"
                  />
                </div>
                
                <div className="privacy-checkbox">
                  <input
                    type="checkbox"
                    id="privacy"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    data-testid="privacy-checkbox"
                  />
                  <label htmlFor="privacy">
                    Li e aceito a <a href="/politica-privacidade">Política de Privacidade</a>
                  </label>
                </div>
                
                {formStatus.error && (
                  <div className="form-error-new" data-testid="form-error">{formStatus.error}</div>
                )}
                
                <button 
                  type="submit" 
                  className="submit-btn-new"
                  disabled={formStatus.loading}
                  data-testid="submit-btn"
                >
                  {formStatus.loading ? 'A ENVIAR...' : (
                    <>
                      ENVIAR MENSAGEM
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ContactsPage;
